import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import HoverableImageThumbnail from '../../catalog/productImages/HoverableImageThumbnail';
import {
  FulfillmentLineExceptionType,
  FulfillmentLineStatus,
  LineItem,
  LineItemLocalState,
} from '../../../services/fulfillment/types';
import { LineStatusEditor } from './LineStatusEditor';
import { LocationCell } from './LocationCell';
import { InventoryIcon } from './InventoryIcon';

export const GRID_COLUMNS = '60px 1fr 70px 210px 240px';
export const MAX_WIDTH = '960px';

interface Props {
  item: LineItem;
  ls: LineItemLocalState | undefined;
  isRemoved: boolean;
  inWave: boolean;
  saving: boolean;
  qcZoneTypes: string[];
  binWarning: string | undefined;
  onClearWarning: () => void;
  onStatusChange: (newStatus: FulfillmentLineStatus) => void;
  onPatchLocalState: (patch: Partial<LineItemLocalState>) => void;
  onSave: (payload: {
    status: FulfillmentLineStatus;
    fulfillmentBinCode?: string;
    exceptionType?: FulfillmentLineExceptionType;
    notes?: string;
  }) => void;
  onOpenInventory: () => void;
  onOpenRelocate: () => void;
}

export const LineItemRow: React.FC<Props> = ({
  item,
  ls,
  isRemoved,
  inWave,
  saving,
  qcZoneTypes,
  binWarning,
  onClearWarning,
  onStatusChange,
  onPatchLocalState,
  onSave,
  onOpenInventory,
  onOpenRelocate,
}) => {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: GRID_COLUMNS,
        gap: 2,
        alignItems: 'start',
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'grey.200',
        maxWidth: MAX_WIDTH,
        ...(isRemoved && { bgcolor: 'grey.100', opacity: 0.75 }),
      }}
    >
      <HoverableImageThumbnail
        src={item.imageUrl ?? undefined}
        identifier={item.sku ?? undefined}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: '0.5px' }}>
            {item.sku ?? '—'}
          </Typography>
          {item.binLocations.length > 0 && (
            <Tooltip title="View inventory locations">
              <IconButton
                size="small"
                onClick={onOpenInventory}
                sx={{ color: 'grey.500', '&:hover': { color: 'primary.main' } }}
              >
                <InventoryIcon />
              </IconButton>
            </Tooltip>
          )}
          {item.sku && (
            <Tooltip title="Relocate item">
              <IconButton
                size="small"
                onClick={onOpenRelocate}
                sx={{ color: 'grey.400', '&:hover': { color: 'warning.main' } }}
              >
                <SwapHorizIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {item.title && (
          <Typography sx={{ fontSize: 13, color: 'grey.600' }}>{item.title}</Typography>
        )}
        {item.upc && (
          <Typography sx={{ fontSize: 11, mt: 0.25 }}>
            <Box component="span" sx={{ fontWeight: 700, color: 'grey.600', mr: '4px' }}>
              UPC
            </Box>
            <Box component="span" sx={{ color: 'grey.500' }}>
              {item.upc}
            </Box>
          </Typography>
        )}
        {item.mpn && (
          <Typography sx={{ fontSize: 11 }}>
            <Box component="span" sx={{ fontWeight: 700, color: 'grey.600', mr: '4px' }}>
              MPN
            </Box>
            <Box component="span" sx={{ color: 'grey.500' }}>
              {item.mpn}
            </Box>
          </Typography>
        )}
      </Box>

      <Typography sx={{ fontSize: 14, color: 'grey.700', whiteSpace: 'nowrap' }}>
        {item.quantity != null ? `×${item.quantity}` : '—'}
      </Typography>

      <Box>
        {ls && (
          <LineStatusEditor
            item={item}
            ls={ls}
            inWave={inWave}
            saving={saving}
            qcZoneTypes={qcZoneTypes}
            binWarning={binWarning}
            onClearWarning={onClearWarning}
            onStatusChange={onStatusChange}
            onPatchLocalState={onPatchLocalState}
            onSave={onSave}
          />
        )}
      </Box>

      <Box>
        {isRemoved && !ls ? (
          <Typography sx={{ fontSize: 13, color: 'grey.500' }}>-</Typography>
        ) : (
          <LocationCell item={item} ls={ls} qcZoneTypes={qcZoneTypes} />
        )}
      </Box>
    </Box>
  );
};
